import { useState, useCallback, useMemo, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'src/components/ui/dialog';
import { Button } from 'src/components/ui/button';
import { Badge } from 'src/components/ui/badge';
import { Checkbox } from 'src/components/ui/checkbox';
import {
  TrashIcon,
  CircleNotchIcon,
  WarningIcon,
  FileTextIcon,
  WarningCircleIcon,
} from '@phosphor-icons/react';
import {
  ProductGroup,
  ProductGroupDeletionImpact,
} from 'src/api/rebates/useProductGroups';
import { formatCurrency } from '@erp/shared-utils';
import { cn } from 'src/lib/utils';
import config from 'src/config';
import { buildPosthogHeaders } from 'src/api/posthogHeaders';
import { formatDateOnlyString } from 'src/utils/dateUtils';
import { Plural, T, Var, useGT } from 'gt-react';

interface ProductGroupDeletionDialogProps {
  open: boolean;
  onClose: () => void;
  productGroup: ProductGroup | null;
  onDeleted?: () => void;
}

export function ProductGroupDeletionDialog({
  open,
  onClose,
  productGroup,
  onDeleted,
}: ProductGroupDeletionDialogProps) {
  const gt = useGT();
  const [deletionImpact, setDeletionImpact] =
    useState<ProductGroupDeletionImpact | null>(null);
  const [loadingImpact, setLoadingImpact] = useState(false);
  const [deletingProductGroup, setDeletingProductGroup] = useState(false);
  const [confirmDeleteWithInvoices, setConfirmDeleteWithInvoices] =
    useState(false);

  // Fetch deletion impact when dialog opens with a product group
  useEffect(() => {
    if (open && productGroup) {
      setLoadingImpact(true);
      setConfirmDeleteWithInvoices(false);
      setDeletionImpact(null);

      const fetchDeletionImpact = async () => {
        try {
          const response = await fetch(
            `${config.apiBaseUrl}/rebates/product-groups/${productGroup.id}/deletion-impact`,
            {
              method: 'GET',
              credentials: 'include',
              headers: buildPosthogHeaders(),
            },
          );

          if (!response.ok) {
            throw new Error('Failed to fetch deletion impact');
          }

          const result = await response.json();
          setDeletionImpact(result.data as ProductGroupDeletionImpact);
        } catch (error) {
          console.error('Error fetching deletion impact:', error);
        } finally {
          setLoadingImpact(false);
        }
      };

      fetchDeletionImpact();
    } else if (!open) {
      // Reset state when dialog closes
      setDeletionImpact(null);
      setConfirmDeleteWithInvoices(false);
    }
  }, [open, productGroup]);

  // Close handler that resets state
  const handleClose = useCallback(() => {
    setDeletionImpact(null);
    setConfirmDeleteWithInvoices(false);
    onClose();
  }, [onClose]);

  // Format date helper (UTC) - handles date-only strings with UTC interpretation
  const formatDate = (dateStr: string | null) => {
    if (!dateStr)
      return gt('-', {
        context: 'product group deletion dialog empty date placeholder',
      });
    // Ensure UTC interpretation for date-only strings (YYYY-MM-DD format)
    // by appending T00:00:00Z, then extract the date part for formatting
    const date = new Date(dateStr + 'T00:00:00Z');
    const dateOnly = date.toISOString().split('T')[0];
    return formatDateOnlyString(dateOnly, '-');
  };

  // Perform the actual deletion
  const confirmDeleteProductGroup = useCallback(async () => {
    if (!productGroup) return;

    const hasInvoices = deletionImpact && deletionImpact.invoices.length > 0;

    // If there are invoices, require confirmation checkbox
    if (hasInvoices && !confirmDeleteWithInvoices) {
      return;
    }

    setDeletingProductGroup(true);

    try {
      const queryParams = hasInvoices ? '?forceDeleteInvoices=true' : '';
      const response = await fetch(
        `${config.apiBaseUrl}/rebates/product-groups/${productGroup.id}${queryParams}`,
        {
          method: 'DELETE',
          credentials: 'include',
          headers: buildPosthogHeaders(),
        },
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to delete product group');
      }

      handleClose();
      onDeleted?.();
    } catch (error) {
      console.error('Error deleting product group:', error);
      if (error instanceof Error) {
        alert(
          gt('Error: {message}', {
            message: error.message,
            context: 'product group deletion error alert',
          }),
        );
      }
    } finally {
      setDeletingProductGroup(false);
    }
  }, [
    productGroup,
    deletionImpact,
    confirmDeleteWithInvoices,
    handleClose,
    onDeleted,
    gt,
  ]);

  // Group rules by agreement for the deletion impact dialog
  const rulesGroupedByAgreement = useMemo(() => {
    if (!deletionImpact?.rules) return [];

    const grouped = new Map<
      string,
      {
        agreementId: string;
        agreementName: string;
        rules: typeof deletionImpact.rules;
      }
    >();

    for (const rule of deletionImpact.rules) {
      const existing = grouped.get(rule.agreementId);
      if (existing) {
        existing.rules.push(rule);
      } else {
        grouped.set(rule.agreementId, {
          agreementId: rule.agreementId,
          agreementName: rule.agreementName,
          rules: [rule],
        });
      }
    }

    // Sort agreements alphabetically
    return Array.from(grouped.values()).sort((a, b) =>
      a.agreementName.localeCompare(b.agreementName),
    );
  }, [deletionImpact?.rules]);

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <TrashIcon className="h-5 w-5 text-destructive" />
            <T context="product group deletion dialog title">
              Delete Product Group
            </T>
          </DialogTitle>
          <DialogDescription>
            {productGroup && (
              <span>
                <T context="product group deletion dialog description">
                  You are about to delete{' '}
                  <strong>
                    <Var>{productGroup.name}</Var>
                  </strong>
                </T>
              </span>
            )}
          </DialogDescription>
        </DialogHeader>

        {loadingImpact ? (
          <div className="flex items-center justify-center py-8">
            <CircleNotchIcon className="h-6 w-6 animate-spin text-muted-foreground" />
            <span className="ml-2 text-muted-foreground">
              <T context="product group deletion loading impact">
                Analyzing impact...
              </T>
            </span>
          </div>
        ) : deletionImpact ? (
          <div className="space-y-4">
            {/* Summary */}
            <div className="grid grid-cols-2 gap-4 p-4 bg-muted/50 rounded-lg">
              <div>
                <p className="text-sm text-muted-foreground">
                  <T context="product group deletion impact summary label">
                    Invoices
                  </T>
                </p>
                <p className="text-2xl font-bold">
                  {deletionImpact.summary.invoiceCount}
                </p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">
                  <T context="product group deletion impact summary label">
                    Total Line Items
                  </T>
                </p>
                <p className="text-2xl font-bold">
                  {deletionImpact.summary.totalLineItemsToDelete}
                </p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">
                  <T context="product group deletion impact summary label">
                    Rules Affected
                  </T>
                </p>
                <p className="text-2xl font-bold">
                  {deletionImpact.summary.ruleCount}
                </p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">
                  <T context="product group deletion impact summary label">
                    Agreements Affected
                  </T>
                </p>
                <p className="text-2xl font-bold">
                  {deletionImpact.summary.agreementCount}
                </p>
              </div>
            </div>

            {/* Invoices to be deleted */}
            {deletionImpact.invoices.length > 0 && (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-destructive">
                    <WarningIcon className="h-4 w-4" />
                    <h4 className="font-semibold">
                      <T context="product group deletion invoices section title">
                        Invoices that will be DELETED
                      </T>
                    </h4>
                  </div>
                  {deletionImpact.summary.lineItemDateRange && (
                    <span className="text-xs text-muted-foreground">
                      <T context="product group deletion invoices date range label">
                        Line item date range:{' '}
                        <Var>
                          {formatDate(
                            deletionImpact.summary.lineItemDateRange.earliest,
                          )}
                        </Var>{' '}
                        -{' '}
                        <Var>
                          {formatDate(
                            deletionImpact.summary.lineItemDateRange.latest,
                          )}
                        </Var>
                      </T>
                    </span>
                  )}
                </div>
                <div className="border rounded-md max-h-48 overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-muted sticky top-0">
                      <tr>
                        <th className="text-left p-2">
                          <T context="product group deletion invoices table column">
                            Invoice #
                          </T>
                        </th>
                        <th className="text-left p-2">
                          <T context="product group deletion invoices table column">
                            Line Item Date Range
                          </T>
                        </th>
                        <th className="text-right p-2">
                          <T context="product group deletion invoices table column">
                            Amount
                          </T>
                        </th>
                        <th className="text-right p-2">
                          <T context="product group deletion invoices table column">
                            Line Items
                          </T>
                        </th>
                        <th className="text-right p-2">
                          <T context="product group deletion invoices table column">
                            Affected
                          </T>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {deletionImpact.invoices.map((invoice) => (
                        <tr
                          key={invoice.id}
                          className="border-t hover:bg-muted/50"
                        >
                          <td className="p-2 flex items-center gap-1">
                            <FileTextIcon className="h-3 w-3 text-muted-foreground" />
                            {invoice.invoiceNumber}
                          </td>
                          <td className="p-2 text-muted-foreground text-xs">
                            {invoice.lineItemDateRange ? (
                              <>
                                {formatDate(invoice.lineItemDateRange.earliest)}
                                {' - '}
                                {formatDate(invoice.lineItemDateRange.latest)}
                              </>
                            ) : (
                              <T context="product group deletion empty table cell">
                                -
                              </T>
                            )}
                          </td>
                          <td className="text-right p-2">
                            {formatCurrency(invoice.totalAmount)}
                          </td>
                          <td className="text-right p-2">
                            {invoice.lineItemCount}
                          </td>
                          <td className="text-right p-2">
                            {invoice.affectedLineItemCount}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Rules affected - grouped by agreement */}
            {deletionImpact.rules.length > 0 && (
              <div className="space-y-2">
                <h4 className="font-semibold flex items-center gap-2">
                  <WarningCircleIcon className="h-4 w-4 text-yellow-500" />
                  <T context="product group deletion rules affected section title">
                    Rules that will be affected (
                    <Var>{deletionImpact.rules.length}</Var>{' '}
                    <Plural
                      n={deletionImpact.rules.length}
                      one="rule"
                      other="rules"
                    />{' '}
                    in <Var>{rulesGroupedByAgreement.length}</Var>{' '}
                    <Plural
                      n={rulesGroupedByAgreement.length}
                      one="agreement"
                      other="agreements"
                    />
                    )
                  </T>
                </h4>
                <div className="space-y-3 max-h-60 overflow-y-auto">
                  {rulesGroupedByAgreement.map((group) => (
                    <div
                      key={group.agreementId}
                      className="border rounded-md overflow-hidden"
                    >
                      {/* Agreement header */}
                      <div className="bg-muted px-3 py-2 border-b">
                        <h5 className="font-medium text-sm">
                          {group.agreementName}
                        </h5>
                      </div>
                      {/* Rules in this agreement */}
                      <div className="p-2 space-y-2">
                        {group.rules.map((rule) => (
                          <div
                            key={rule.id}
                            className={cn(
                              'p-2 rounded-md border',
                              rule.impact === 'will_become_invalid' &&
                                'border-destructive bg-destructive/10',
                              rule.impact === 'behavior_change' &&
                                'border-yellow-500 bg-yellow-50',
                              rule.impact === 'minor' &&
                                'border-gray-200 bg-gray-50',
                            )}
                          >
                            <div className="flex items-center justify-between gap-2">
                              <p className="font-medium text-sm">{rule.name}</p>
                              <Badge
                                variant={
                                  rule.impact === 'will_become_invalid'
                                    ? 'destructive'
                                    : rule.impact === 'behavior_change'
                                      ? 'default'
                                      : 'secondary'
                                }
                                className={cn(
                                  'text-xs shrink-0',
                                  rule.impact === 'behavior_change' &&
                                    'bg-yellow-500 hover:bg-yellow-600',
                                )}
                              >
                                {rule.impact === 'will_become_invalid' ? (
                                  <T context="product group rule impact badge">
                                    Invalid
                                  </T>
                                ) : rule.impact === 'behavior_change' ? (
                                  <T context="product group rule impact badge">
                                    Behavior Change
                                  </T>
                                ) : (
                                  <T context="product group rule impact badge">
                                    Minor
                                  </T>
                                )}
                              </Badge>
                            </div>
                            <p className="text-xs mt-1 text-muted-foreground">
                              {rule.impactReason}
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Confirmation checkbox for invoice deletion */}
            {deletionImpact.invoices.length > 0 && (
              <div className="flex items-start gap-2 p-4 bg-destructive/10 rounded-md border border-destructive">
                <Checkbox
                  id="confirm-delete-invoices"
                  checked={confirmDeleteWithInvoices}
                  onCheckedChange={(checked) =>
                    setConfirmDeleteWithInvoices(checked === true)
                  }
                />
                <label
                  htmlFor="confirm-delete-invoices"
                  className="text-sm cursor-pointer"
                >
                  <T context="product group deletion confirm with invoices checkbox">
                    I understand that{' '}
                    <strong>
                      <Plural
                        n={deletionImpact.summary.invoiceCount}
                        one="1 invoice"
                        other="# invoices"
                      />
                    </strong>{' '}
                    and{' '}
                    <strong>
                      <Plural
                        n={deletionImpact.summary.totalLineItemsToDelete}
                        one="1 line item"
                        other="# line items"
                      />
                    </strong>{' '}
                    will be permanently deleted. This action cannot be undone.
                  </T>
                </label>
              </div>
            )}

            {/* No impact message */}
            {deletionImpact.invoices.length === 0 &&
              deletionImpact.rules.length === 0 && (
                <div className="p-4 bg-green-50 rounded-md border border-green-200">
                  <p className="text-sm text-green-800">
                    <T context="product group deletion no dependencies message">
                      This product group is not referenced by any invoices or
                      rules. It can be safely deleted.
                    </T>
                  </p>
                </div>
              )}
          </div>
        ) : null}

        <DialogFooter>
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={deletingProductGroup}
          >
            <T context="dialog cancel button">Cancel</T>
          </Button>
          <Button
            variant="destructive"
            onClick={confirmDeleteProductGroup}
            disabled={
              loadingImpact ||
              deletingProductGroup ||
              (deletionImpact !== null &&
                deletionImpact.invoices.length > 0 &&
                !confirmDeleteWithInvoices)
            }
          >
            {deletingProductGroup ? (
              <>
                <CircleNotchIcon className="h-4 w-4 mr-2 animate-spin" />
                <T context="product group deletion in progress button">
                  Deleting...
                </T>
              </>
            ) : (
              <T context="product group deletion confirm button">
                Delete Product Group
              </T>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
